import * as XLSX from 'xlsx';
import type { InvoiceImportKind, InvoiceImportRow } from '@/api/invoiceImport.api';
import { normalise, readAmount, readDate, readText } from '@/utils/journalImportSheet';

/**
 * The AR invoice / AP bill import spreadsheet (422471): its template, and reading one back.
 * Like the journal import, this only reads cells; whether any of it is an invoice is the server's call.
 */

interface ColumnSpec { key: keyof InvoiceImportRow; header: string; aliases?: string[]; width: number; note: string }

const columns = (kind: InvoiceImportKind): ColumnSpec[] => [
  { key: 'invoiceRef',  header: 'Invoice Ref', aliases: ['invoice', 'ref', 'invoice no'], width: 14,
    note: 'Rows sharing a value become one invoice' },
  { key: 'partyCode',   header: kind === 'AR' ? 'Customer Code' : 'Supplier Code',
    aliases: [kind === 'AR' ? 'customer' : 'supplier'], width: 16, note: 'Must exist in myLedger' },
  { key: 'invoiceDate', header: 'Invoice Date', aliases: ['date', 'bill date'], width: 14, note: 'yyyy-mm-dd' },
  { key: 'postingDate', header: 'Posting Date', width: 14, note: 'Optional — defaults to the invoice date' },
  { key: 'dueDate',     header: 'Due Date', width: 14, note: 'Optional' },
  ...(kind === 'AP'
    ? [{ key: 'supplierInvoiceNumber', header: 'Supplier Invoice No', aliases: ['supplier invoice'], width: 18,
         note: 'Optional — refused if already in myLedger' } as ColumnSpec]
    : []),
  { key: 'currencyCode', header: 'Currency', width: 10, note: 'Optional — base currency if blank' },
  { key: 'exchangeRate', header: 'Exchange Rate', aliases: ['rate'], width: 12, note: 'Required for a foreign currency' },
  { key: 'narration',    header: 'Narration', width: 28, note: 'Optional' },
  { key: 'locationCode', header: 'Location', aliases: ['location code'], width: 12, note: 'Blank = your session location' },
  { key: 'costCenterCode', header: 'Cost Centre', aliases: ['cost center'], width: 12, note: 'Blank = your session cost centre' },
  { key: 'accountCode',  header: 'Account Code', aliases: ['account', 'gl account'], width: 14,
    note: kind === 'AR' ? 'Income account of the line' : 'Expense / inventory account of the line' },
  { key: 'description',  header: 'Description', width: 28, note: 'Optional' },
  { key: 'quantity',     header: 'Quantity', aliases: ['qty'], width: 10, note: 'More than zero' },
  { key: 'unitPrice',    header: 'Unit Price', aliases: ['price'], width: 12, note: 'Before tax' },
  { key: 'discountPercent', header: 'Discount %', aliases: ['discount'], width: 11, note: 'Optional, 0-100' },
  { key: 'taxCode',      header: 'Tax Code', aliases: ['tax'], width: 11, note: 'Optional — e.g. VAT18' },
];

export function downloadInvoiceImportTemplate(kind: InvoiceImportKind): void {
  const cols = columns(kind);
  // Two lines of one invoice, so the "rows share an Invoice Ref" rule is obvious from the example.
  const example = (line: number): Partial<Record<keyof InvoiceImportRow, unknown>> => ({
    invoiceRef: 'INV-001', partyCode: kind === 'AR' ? 'C0001' : 'S0001', invoiceDate: '2026-10-21',
    supplierInvoiceNumber: 'SI-5521', narration: 'October services',
    accountCode: kind === 'AR' ? '4000' : '6201', description: `Line ${line}`,
    quantity: line, unitPrice: 1000, discountPercent: 0,
  });
  const aoa = [cols.map(c => c.header), cols.map(c => c.note), ...[1, 2].map(l => cols.map(c => example(l)[c.key] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map(c => ({ wch: c.width }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, kind === 'AR' ? 'AR Invoices' : 'AP Bills');
  XLSX.writeFile(wb, kind === 'AR' ? 'ar-invoice-import-template.xlsx' : 'ap-bill-import-template.xlsx');
}

export async function parseInvoiceImportFile(kind: InvoiceImportKind, file: File): Promise<InvoiceImportRow[]> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0] ?? ''];
  if (!sheet) throw new Error('That file has no worksheets.');
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });

  const cols = columns(kind);
  const index = new Map(cols.flatMap(c => [c.header, ...(c.aliases ?? [])].map(h => [normalise(h), c.key] as const)));

  // The header row may sit below a title; it is the first row naming Invoice Ref and Account Code.
  const headerAt = grid.slice(0, 20).findIndex(r => {
    const keys = (r ?? []).map(c => index.get(normalise(String(c ?? ''))));
    return keys.includes('invoiceRef') && keys.includes('accountCode');
  });
  if (headerAt === -1)
    throw new Error('Could not find the header row (Invoice Ref, Account Code…). Download the template to see the layout.');
  const keys = (grid[headerAt] ?? []).map(c => index.get(normalise(String(c ?? ''))));

  const rows: InvoiceImportRow[] = [];
  for (let i = headerAt + 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const cell = (key: keyof InvoiceImportRow): unknown => {
      const at = keys.indexOf(key);
      return at === -1 ? null : cells[at];
    };
    const invoiceRef = readText(cell('invoiceRef'));
    // The template's note row and blank lines carry no usable invoice ref — skip them.
    if (!invoiceRef || /rows sharing/i.test(invoiceRef)) continue;
    const rate = readAmount(cell('exchangeRate'));
    rows.push({
      rowNumber: i + 1,   // Excel's own row number, so "Row 14" points at the line the user sees
      invoiceRef,
      partyCode:             readText(cell('partyCode')),
      invoiceDate:           readDate(cell('invoiceDate')),
      postingDate:           readDate(cell('postingDate')),
      dueDate:               readDate(cell('dueDate')),
      supplierInvoiceNumber: readText(cell('supplierInvoiceNumber')),
      currencyCode:          readText(cell('currencyCode'))?.toUpperCase() ?? null,
      exchangeRate:          rate > 0 ? rate : null,
      narration:             readText(cell('narration')),
      locationCode:          readText(cell('locationCode')),
      costCenterCode:        readText(cell('costCenterCode')),
      accountCode:           readText(cell('accountCode')),
      description:           readText(cell('description')),
      quantity:              readAmount(cell('quantity')),
      unitPrice:             readAmount(cell('unitPrice')),
      discountPercent:       readAmount(cell('discountPercent')),
      taxCode:               readText(cell('taxCode')),
    });
  }
  if (rows.length === 0) throw new Error('No invoice rows found below the header.');
  return rows;
}
